import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useCare } from '../../contexts/CareContext'
import { useToast, humanizeError } from '../shared/Toast'

const ACCEPT = 'image/jpeg,image/png,image/heic,image/heif,image/webp'
const MAX_FILE_BYTES = 10 * 1024 * 1024
// Sonnet 5 reads up to 2576px on the long edge; past that is wasted tokens.
const MAX_EDGE = 2576

let rowSeq = 0
const blankMed = () => ({
  _id: `r${++rowSeq}`, name: '', dose: '', timing: '', purpose: '', prescribing_doctor: '',
})

/**
 * Downscale to JPEG in the browser before upload.
 *
 * Two jobs: keep the payload under Anthropic's 5MB base64 image cap (a raw
 * iPhone photo blows straight past it), and convert HEIC — which Anthropic
 * does not accept — into JPEG. Canvas export handles both in one step.
 */
function toResizedBase64(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height))
      const w = Math.max(1, Math.round(img.width * scale))
      const h = Math.max(1, Math.round(img.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, w, h)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9)
      resolve({ base64: dataUrl.split(',')[1], preview: dataUrl })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      // Mostly HEIC on a browser that can't decode it (anything but Safari).
      reject(new Error("This browser can't read that image format. Try a JPEG or PNG."))
    }
    img.src = url
  })
}

export default function MedicationExtractor({ onCancel, onDone }) {
  const { recipient } = useCare()
  const toast = useToast()
  const navigate = useNavigate()

  const [step, setStep] = useState('upload') // upload | review
  const [preview, setPreview] = useState(null)
  const [reading, setReading] = useState(false)
  const [meds, setMeds] = useState([])
  const [confidence, setConfidence] = useState(null)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const name = recipient?.preferred_name || recipient?.full_name || 'their'

  async function onFile(file) {
    if (!file) return
    if (file.size > MAX_FILE_BYTES) return toast.error('Image must be under 10MB.')
    setReading(true)
    try {
      const { base64, preview: dataUrl } = await toResizedBase64(file)
      setPreview(dataUrl)

      const { data, error } = await supabase.functions.invoke('extract-medications', {
        body: { image_base64: base64, media_type: 'image/jpeg' },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)

      const found = (data?.medications || []).map((m) => ({ ...blankMed(), ...m }))
      setConfidence(data?.confidence || 'low')
      setNotes(data?.notes || '')

      if (found.length === 0) {
        toast.error(data?.notes || 'No medications found in that image.')
        setMeds([blankMed()])
      } else {
        setMeds(found)
      }
      setStep('review')
    } catch (err) {
      toast.error(humanizeError(err))
      setPreview(null)
    } finally {
      setReading(false)
    }
  }

  const update = (id, field, value) =>
    setMeds((list) => list.map((m) => (m._id === id ? { ...m, [field]: value } : m)))

  const ready = meds.filter((m) => m.name.trim())

  async function save() {
    if (!ready.length) return toast.error('Add at least one medication.')
    setSaving(true)
    try {
      // NB: `medications` has no created_by column — care_recipient_id is the
      // only ownership link, and RLS gates the write.
      const rows = ready.map((m) => ({
        care_recipient_id: recipient.id,
        name: m.name.trim(),
        dose: m.dose.trim() || null,
        timing: m.timing.trim() || null,
        purpose: m.purpose.trim() || null,
        prescribing_doctor: m.prescribing_doctor.trim() || null,
        is_active: true,
      }))
      const { error } = await supabase.from('medications').insert(rows)
      if (error) throw error
      toast.success(`${rows.length} medication${rows.length > 1 ? 's' : ''} added to ${name}'s profile.`)
      onDone?.()
      navigate('/medications')
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="stack">
      <div className="spread">
        <h2 className="title" style={{ fontSize: 22 }}>
          {step === 'upload' ? 'Scan prescription' : 'Check what Ginie read'}
        </h2>
        <button
          onClick={onCancel} aria-label="Close"
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 24, lineHeight: 1 }}
        >
          ×
        </button>
      </div>

      {step === 'upload' && (
        <div className="stack">
          <p className="muted" style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>
            Photograph a prescription or the label on a medication bottle. You&rsquo;ll
            get a chance to check and correct everything before it&rsquo;s saved.
          </p>

          {preview && (
            /* The thumbnail stays visible behind the overlay so it is obvious
               which photo is being read. */
            <div style={{ position: 'relative' }}>
              <img
                src={preview} alt="Prescription preview"
                style={{
                  width: '100%', maxHeight: 240, objectFit: 'contain',
                  borderRadius: 10, background: 'var(--bg)',
                  filter: reading ? 'blur(2px)' : 'none',
                }}
              />
              {reading && (
                <div
                  role="status" aria-live="polite"
                  style={{
                    position: 'absolute', inset: 0, borderRadius: 10,
                    background: 'rgba(255,255,255,0.92)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16,
                  }}
                >
                  <i
                    className="ti ti-sparkles rx-pulse"
                    aria-hidden
                    style={{ fontSize: 34, color: 'var(--teal)', lineHeight: 1 }}
                  />
                  <div style={{ fontWeight: 700, fontSize: 15, textAlign: 'center' }}>
                    Ginie is reading this prescription…
                  </div>
                  <div className="rx-dots" aria-hidden>
                    <span /><span /><span />
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    This usually takes 10&ndash;15 seconds
                  </div>
                </div>
              )}
            </div>
          )}

          {reading ? null : (
            <div className="row wrap" style={{ gap: 8 }}>
              <label className="btn grow" style={{ cursor: 'pointer', textAlign: 'center' }}>
                📷 Take photo
                <input
                  type="file" accept={ACCEPT} capture="environment" hidden
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onFile(f) }}
                />
              </label>
              <label className="btn btn-secondary grow" style={{ cursor: 'pointer', textAlign: 'center' }}>
                🖼 Choose from library
                <input
                  type="file" accept={ACCEPT} hidden
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onFile(f) }}
                />
              </label>
            </div>
          )}

          <p className="muted" style={{ fontSize: 12, margin: 0 }}>
            JPEG, PNG or HEIC · up to 10MB. Photos are resized before sending and are not stored.
          </p>
        </div>
      )}

      {step === 'review' && (
        <div className="stack">
          {confidence === 'low' && (
            <div className="card" style={{ borderColor: 'var(--amber)', background: 'var(--amber-light)' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Review carefully</div>
              <div style={{ fontSize: 14, marginTop: 4, lineHeight: 1.5 }}>
                Ginie wasn&rsquo;t fully confident about this prescription. Check every
                name and dose against the label before saving.
              </div>
            </div>
          )}

          {notes && (
            <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>{notes}</p>
          )}

          {meds.map((m, i) => (
            <div key={m._id} className="card stack" style={{ gap: 8 }}>
              <div className="spread">
                <span className="muted" style={{ fontSize: 12 }}>Medication {i + 1}</span>
                <button
                  type="button"
                  onClick={() => setMeds((list) => list.filter((x) => x._id !== m._id))}
                  aria-label={`Remove ${m.name || 'medication'}`}
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 18, cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>
              <input
                className="input" value={m.name} placeholder="Name *"
                aria-label="Medication name"
                onChange={(e) => update(m._id, 'name', e.target.value)}
              />
              <div className="row" style={{ gap: 8 }}>
                <input
                  className="input grow" value={m.dose} placeholder="Dose (e.g. 10mg)"
                  aria-label="Dose" onChange={(e) => update(m._id, 'dose', e.target.value)}
                />
                <input
                  className="input grow" value={m.timing} placeholder="Timing"
                  aria-label="Timing" onChange={(e) => update(m._id, 'timing', e.target.value)}
                />
              </div>
              <input
                className="input" value={m.purpose} placeholder="Purpose (optional)"
                aria-label="Purpose" onChange={(e) => update(m._id, 'purpose', e.target.value)}
              />
              <input
                className="input" value={m.prescribing_doctor} placeholder="Prescribed by (optional)"
                aria-label="Prescribing doctor"
                onChange={(e) => update(m._id, 'prescribing_doctor', e.target.value)}
              />
            </div>
          ))}

          <button
            type="button" className="btn btn-ghost btn-block"
            onClick={() => setMeds((list) => [...list, blankMed()])}
          >
            + Add another
          </button>

          <div className="row" style={{ gap: 10 }}>
            <button className="btn btn-ghost" onClick={() => { setStep('upload'); setPreview(null) }} disabled={saving}>
              Back
            </button>
            <button className="btn btn-lg grow" onClick={save} disabled={saving || !ready.length}>
              {saving
                ? <span className="spinner" />
                : `Add ${ready.length} medication${ready.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
